'use es6';

import { Map as ImmutableMap, OrderedSet } from 'immutable';
import Raven from 'raven-js';
import ActionTypes from './ActionTypes';
import { makeDebouncedAction } from '../utils/ActionUtils';
import { ACADEMY_TRACKS_REDUCER_KEY } from '../constants/CourseConstants';
import { deleteLessonFavorite, deleteTrackFavorite, getFavorites, setLessonFavorite, setTrackFavorite } from '../features/favorites';
try {
  localStorage.removeItem(ACADEMY_TRACKS_REDUCER_KEY);
} catch (error) {
  Raven.captureException(error);
}
import UsageTracker from '../utils/UsageTracker';
export const getFavoritesRequesting = () => ({
  type: ActionTypes.BOOKMARKS_GET_REQUESTING
});
export const getFavoritesReceived = response => ({
  type: ActionTypes.BOOKMARKS_GET_RECEIVED,
  payload: {
    favorites: OrderedSet(response.bookmarks.map(ImmutableMap))
  }
});
export const getFavoritesError = () => ({
  type: ActionTypes.BOOKMARKS_GET_ERROR
});
export const editTrackFavoriteRequesting = trackId => ({
  type: ActionTypes.BOOKMARKS_EDIT_TRACK_REQUESTING,
  payload: {
    trackId
  }
});
export const addTrackFavoriteReceived = (trackId, showTooltip = true) => ({
  type: ActionTypes.BOOKMARKS_ADD_TRACK_RECEIVED,
  payload: {
    trackId
  },
  meta: {
    showTooltip
  }
});
export const removeTrackFavoriteReceived = (trackId, showTooltip = true) => ({
  type: ActionTypes.BOOKMARKS_REMOVE_TRACK_RECEIVED,
  payload: {
    trackId
  },
  meta: {
    showTooltip
  }
});
export const editTrackFavoriteError = (error, trackId) => ({
  type: ActionTypes.BOOKMARKS_EDIT_TRACK_ERROR,
  payload: {
    trackId
  }
});
export const editLessonFavoriteRequesting = lessonId => ({
  type: ActionTypes.BOOKMARKS_EDIT_LESSON_REQUESTING,
  payload: {
    lessonId
  }
});
export const addLessonFavoriteReceived = (lessonId, showTooltip = true) => ({
  type: ActionTypes.BOOKMARKS_ADD_LESSON_RECEIVED,
  payload: {
    lessonId
  },
  meta: {
    showTooltip
  }
});
export const removeLessonFavoriteReceived = (lessonId, showTooltip = true) => ({
  type: ActionTypes.BOOKMARKS_REMOVE_LESSON_RECEIVED,
  payload: {
    lessonId
  },
  meta: {
    showTooltip
  }
});
export const editLessonFavoriteError = (error, lessonId) => ({
  type: ActionTypes.BOOKMARKS_EDIT_LESSON_ERROR,
  payload: {
    lessonId,
    error
  }
});
export const clearFavoriteTooltip = () => ({
  type: ActionTypes.BOOKMARKS_CLEAR_TOOLTIP
});
let favoriteTooltipTimeout = null;
export const delayedClearFavoriteTooltip = () => dispatch => {
  clearTimeout(favoriteTooltipTimeout);
  favoriteTooltipTimeout = setTimeout(() => dispatch(clearFavoriteTooltip()), 3000);
};
export const fetchFavoritesDebounced = makeDebouncedAction(dispatch => {
  dispatch(getFavoritesRequesting());
  getFavorites().then(response => {
    dispatch(getFavoritesReceived({
      bookmarks: response.bookmarks.filter(favorite => !favorite.deletionTime)
    }));
  }, errorResponse => dispatch(getFavoritesError(errorResponse.networkError))).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return undefined;
}, 1000 * 3, {
  leading: true,
  trailing: false
});
export const addTrackFavorite = (contentLanguage, trackId, showTooltip = true) => dispatch => {
  dispatch(editTrackFavoriteRequesting(trackId));
  dispatch(clearFavoriteTooltip());
  setTrackFavorite(trackId).then(() => {
    dispatch(addTrackFavoriteReceived(trackId, showTooltip));
    dispatch(delayedClearFavoriteTooltip());
    UsageTracker.track('interaction', {
      action: `favorite-added`,
      contentLanguage,
      trackId
    });
  }, error => dispatch(editTrackFavoriteError(error, trackId)) //potentially call fetchFavorites again to be back in sync, or add it to the reducer if the already exists error comes back
  ).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return undefined;
};
export const removeTrackFavorite = (trackId, showTooltip = true) => dispatch => {
  dispatch(editTrackFavoriteRequesting(trackId));
  dispatch(clearFavoriteTooltip());
  deleteTrackFavorite(trackId).then(() => {
    dispatch(removeTrackFavoriteReceived(trackId, showTooltip));
    dispatch(delayedClearFavoriteTooltip());
    UsageTracker.track('interaction', {
      action: `favorite-removed`,
      trackId
    });
  }, error => dispatch(editTrackFavoriteError(error, trackId)) //potentially call fetchFavorites again to be back in sync, or remove it to the reducer if the already exists error comes back
  ).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return undefined;
};
export const addLessonFavorite = (contentLanguage, lessonId, showTooltip = true) => dispatch => {
  dispatch(editLessonFavoriteRequesting(lessonId));
  dispatch(clearFavoriteTooltip());
  setLessonFavorite(lessonId).then(() => {
    dispatch(addLessonFavoriteReceived(lessonId, showTooltip));
    dispatch(delayedClearFavoriteTooltip());
    UsageTracker.track('interaction', {
      action: `favorite-added`,
      contentLanguage,
      lessonId
    });
  }, error => dispatch(editLessonFavoriteError(error, lessonId)) //potentially call fetchFavorites again to be back in sync, or add it to the reducer if the already exists error comes back
  ).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return undefined;
};
export const removeLessonFavorite = (lessonId, showTooltip = true) => dispatch => {
  dispatch(editLessonFavoriteRequesting(lessonId));
  dispatch(clearFavoriteTooltip());
  deleteLessonFavorite(lessonId).then(() => {
    dispatch(removeLessonFavoriteReceived(lessonId, showTooltip));
    dispatch(delayedClearFavoriteTooltip());
    UsageTracker.track('interaction', {
      action: `favorite-removed`,
      lessonId
    });
  }, error => dispatch(editLessonFavoriteError(error, lessonId)) //potentially call fetchFavorites again to be back in sync, or remove it to the reducer if the already exists error comes back
  ).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return undefined;
};